import { useMemo } from "react";

import { useUserEstateRole } from "@/shared/hooks/useUserEstateRole";
import { buildEssentialsContext } from "@/shared/utils/buildEssentialsContext";
import { processConditionalBlocks } from "@/shared/utils/processConditionalBlocks";
import { resolveGuideTemplate } from "@/shared/utils/resolveGuideTemplate";
import { replaceTemplateVariables } from "@/shared/utils/templateReplacement";

import type { DeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import type { EssentialModule } from "@/shared/types/estateEssentials";
import type { EstateItem } from "@/shared/types/estateTypes";

import { getVehiclePrefix, splitMakeModel, resolveVehicleFormData } from "../utils/vehicleUtils";
import { SAMPLE_GUIDE_TEMPLATE } from "../templates/sampleGuideTemplate";

interface GuideMarkdownParams {
  module: EssentialModule | undefined;
  formData: Record<string, string>;
  deceasedData: DeceasedUserData;
  userFirstName: string;
  userLastName: string;
  relationship: string | undefined;
  isVehicleModule: boolean;
  vehicleItems: EstateItem[];
}

export function useGuideMarkdown({
  module,
  formData,
  deceasedData,
  userFirstName,
  userLastName,
  relationship,
  isVehicleModule,
  vehicleItems,
}: GuideMarkdownParams): string | null {
  const roleData = useUserEstateRole();

  return useMemo(() => {
    if (!module) return null;

    let resolvedFormData = formData;
    if (isVehicleModule && formData.selected_vehicle_id) {
      const sid = formData.selected_vehicle_id;
      const isNew = sid.startsWith("new-");
      const prefix = getVehiclePrefix(sid);
      const selectedVehicle = !isNew ? vehicleItems.find((v) => String(v.id) === sid) : undefined;
      const meta = (selectedVehicle?.metadata ?? {}) as Record<string, unknown>;
      resolvedFormData = {
        ...formData,
        ...resolveVehicleFormData(formData, prefix, meta, isNew),
      };
    }

    const { make, model } = splitMakeModel(resolvedFormData.vehicle_make_model ?? "");
    const contextFormData = {
      ...resolvedFormData,
      user_relationship: relationship || resolvedFormData.user_relationship || "",
      relationship_to_deceased: relationship || resolvedFormData.relationship_to_deceased || "",
      vehicle_make: make || resolvedFormData.vehicle_make || "",
      vehicle_model: model || resolvedFormData.vehicle_model || "",
    };

    const context = buildEssentialsContext(
      module.id,
      contextFormData,
      deceasedData,
      {
        isNominated: roleData.isNominated,
        isTrustee: roleData.isTrustee,
        isExecutor: roleData.isExecutor,
        isAdministrator: roleData.isAdministrator,
        hasLegalAuthority: roleData.hasLegalAuthority,
        legalRole: roleData.legalRole,
      },
      { firstName: userFirstName, lastName: userLastName },
    );

    const stateCode = context.state ?? "";
    const rawTemplate =
      resolveGuideTemplate(module.id, stateCode, roleData.legalRole) ?? SAMPLE_GUIDE_TEMPLATE;
    const withConditionalsResolved = processConditionalBlocks(rawTemplate, context);
    return replaceTemplateVariables(withConditionalsResolved, context);
  }, [module, formData, deceasedData, roleData, relationship, isVehicleModule, vehicleItems]);
}
